import type { ImportJobDto } from '@carbonwebmail/contracts'
import { Alert } from '../../ui/Alert.js'
import { count } from '../../ui/format.js'

/**
 * An import in progress, and what it has done so far.
 *
 * **This screen exists because the last one said "Importing…" and nothing
 * else.** The import reads a board a page at a time and writes contacts as it
 * goes; on a 4,763-item board that was minutes of a button saying nothing, long
 * enough that somebody reloads — which used to start a second import.
 *
 * The counts are live, not a summary at the end. They are the same numbers the
 * finished screen shows, because they are the same numbers: what is drawn here
 * while it runs is what is drawn there when it stops.
 */
export function Importing({ job }: { readonly job: ImportJobDto | undefined }) {
  // Before the first answer comes back, and before the board has been opened —
  // both are real moments, and both are honest about having no number yet.
  const read = job?.itemsRead ?? 0
  const total = job?.itemsTotal ?? undefined
  const done = total === undefined || total === 0 ? undefined : Math.min(1, read / total)

  return (
    <section className="rounded-card border border-line bg-surface p-5">
      <h2 className="mb-1 font-bold text-[15px] text-t1">
        {job?.boardName === null || job?.boardName === undefined
          ? 'Importing'
          : `Importing ${job.boardName}`}
      </h2>
      <p className="mb-4 text-[13px] text-t2">
        {job?.status === 'queued'
          ? 'Queued. It will start in a moment.'
          : 'This runs on the server, so you can leave this page and come back.'}
      </p>

      {/*
        A bar only once there is a denominator. monday's own count for the board
        arrives a second or two in; a bar that filled against a total invented
        from nothing would be a progress bar that lies, which is worse than a
        number on its own.
      */}
      {done === undefined ? (
        <p className="mb-4 font-num text-[13px] text-t2 tabular-nums">{count(read)} read so far</p>
      ) : (
        <>
          <div className="mb-2 h-2 w-full overflow-hidden rounded-pill bg-surface-2">
            <div
              className={`h-full rounded-pill bg-brand transition-all duration-500 ${WIDTHS[step(done)]}`}
            />
          </div>
          <p className="mb-4 font-num text-[13px] text-t2 tabular-nums">
            {count(read)} of {count(total ?? 0)} read
          </p>
        </>
      )}

      <dl className="grid gap-3 sm:grid-cols-4">
        <Figure label="Added" value={job?.added ?? 0} />
        <Figure label="Already there" value={job?.duplicates ?? 0} />
        <Figure label="No address" value={job?.withoutEmail ?? 0} />
        <Figure label="Unsubscribed" value={job?.unsubscribed ?? 0} />
      </dl>

      {job?.segments === undefined || job.segments.length === 0 ? null : (
        <p className="mt-4 text-[13px] text-t2">
          Segments: <strong className="font-semibold text-t1">{job.segments.join(', ')}</strong>
        </p>
      )}

      <div className="mt-4">
        <Alert tone="unknown">
          Leaving this page does not stop it. What has been read is already in — reopening the board
          shows where it got to.
        </Alert>
      </div>
    </section>
  )
}

/** A twentieth of the track per step, which is finer than an eye follows. */
function step(done: number): number {
  return Math.round(done * 20)
}

/**
 * Literal classes, never assembled.
 *
 * Tailwind generates classes by scanning source text, so a width built as
 * `w-[${percent}%]` is a class that does not exist in the stylesheet and
 * renders at zero — the same trap `BarChart`'s heights and `Table`'s minimum
 * widths both document. A bar that moves in twentieths is a worse bar than one
 * that moves smoothly, and a far better one than a bar that never moves.
 *
 * An inline `style` would give exact widths and is what Biome's `noInlineStyles`
 * exists to refuse, for the reason this file would otherwise demonstrate: a
 * style attribute is a second styling system that no design token reaches.
 */
const WIDTHS: Record<number, string> = {
  0: 'w-0',
  1: 'w-[5%]',
  2: 'w-[10%]',
  3: 'w-[15%]',
  4: 'w-[20%]',
  5: 'w-[25%]',
  6: 'w-[30%]',
  7: 'w-[35%]',
  8: 'w-[40%]',
  9: 'w-[45%]',
  10: 'w-1/2',
  11: 'w-[55%]',
  12: 'w-[60%]',
  13: 'w-[65%]',
  14: 'w-[70%]',
  15: 'w-[75%]',
  16: 'w-[80%]',
  17: 'w-[85%]',
  18: 'w-[90%]',
  19: 'w-[95%]',
  20: 'w-full',
}

function Figure({ label, value }: { readonly label: string; readonly value: number }) {
  return (
    <div className="rounded-card border border-line bg-surface-2 px-4 py-3">
      <dt className="font-semibold text-[12px] text-t3 uppercase tracking-wide">{label}</dt>
      <dd className="mt-1 font-bold font-num text-[20px] text-t1 tabular-nums">{count(value)}</dd>
    </div>
  )
}
